'use client';

import React, { useRef, useState } from 'react';

import type {
  BookPayload,
  CardLabels,
  EnquirePayload,
  FlatPackageItem,
} from '../../types';
import PackageCard from '../PackageCard';

interface RowItem {
  key: string;
  pkg: FlatPackageItem;
}

interface CarouselRowProps {
  row: RowItem[];
  labels?: CardLabels;
  onEnquire?: (payload: EnquirePayload) => void;
  onBook?: (payload: BookPayload) => void;
}

export default function CarouselRow({
  row,
  labels,
  onEnquire,
  onBook,
}: CarouselRowProps): React.ReactElement {
  const [openCardIds, setOpenCardIds] = useState<Set<string>>(() => new Set());
  const [frozenHeight, setFrozenHeight] = useState<number | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const handleCardToggle = (key: string, isOpen: boolean): void => {
    const wasAnyOpen = openCardIds.size > 0;

    if (!wasAnyOpen && isOpen) {
      // A card in this row is about to expand for the first time — snapshot
      // the row's current (uniformly stretched) height before the layout
      // changes, so the untouched siblings can be pinned to it instead of
      // shrinking down to their own natural size once stretch is dropped.
      const heights = row
        .map(({ key: rowKey }) => cardRefs.current.get(rowKey)?.offsetHeight)
        .filter((h): h is number => typeof h === 'number');
      if (heights.length) setFrozenHeight(Math.max(...heights));
    }

    setOpenCardIds((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(key);
      else next.delete(key);
      if (next.size === 0) setFrozenHeight(null);
      return next;
    });
  };

  const rowHasOpen = openCardIds.size > 0;

  return (
    <div
      className={`grid grid-cols-2 gap-6 lg:grid-cols-3 ${rowHasOpen ? 'items-start' : 'items-stretch'}`}
    >
      {row.map(({ key, pkg }) => {
        const isThisOpen = openCardIds.has(key);
        const shouldFreeze = rowHasOpen && !isThisOpen && frozenHeight !== null;

        return (
          <div
            key={key}
            ref={(el) => {
              if (el) cardRefs.current.set(key, el);
              else cardRefs.current.delete(key);
            }}
            className="break-inside-avoid pb-6"
            style={
              shouldFreeze
                ? { height: frozenHeight ?? undefined, overflow: 'hidden' }
                : undefined
            }
          >
            <PackageCard
              badge={pkg.badge}
              title={pkg.title}
              tests={pkg.tests}
              testType={pkg.testType}
              gender={pkg.gender}
              price={pkg.price}
              defaultOpen={false}
              labels={labels}
              onToggleOpen={(isOpen) => handleCardToggle(key, isOpen)}
              onEnquire={() =>
                onEnquire?.({
                  category: pkg.title,
                  title: pkg.title,
                  gender: pkg.gender,
                  testType: pkg.testType,
                  price: pkg.price,
                  source: 'health_packages_most_popular',
                })
              }
              onBook={
                pkg.apiIds?.length
                  ? () =>
                      onBook?.({
                        category: pkg.title,
                        title: pkg.title,
                        gender: pkg.gender,
                        testType: pkg.testType,
                        price: pkg.price,
                        source: 'health_packages_most_popular',
                        apiIds: pkg.apiIds,
                        depositFee: pkg.depositFee,
                      })
                  : undefined
              }
            />
          </div>
        );
      })}
    </div>
  );
}
